
import Vue from "vue";
import { API } from "aws-amplify";
import { format } from "date-fns";
import ButtonDefault from "@/components/atoms/ButtonDefault.vue";
import LoadingSpinner from "@/components/atoms/LoadingSpinner.vue";
import CupertinoAlertDialog from "@/components/ui/dialogs/CupertinoAlertDialog.vue";
import PremiumFeatureIntroduce from "@/components/organisms/PremiumFeatureIntroduce.vue";
import { showNotifyDialog } from "@/lib/dialog";
import FileRestoreOutlineIcon from "icons/FileRestoreOutline.vue";

import { Plan } from "@/lib/models";
import { NolaAnalytics } from "@/lib/utils/analytics";

interface HistoryItem {
  historyId: string;
  timestamp: number;
  /** 無料会員で、復元期限切れの場合は true */
  restoreExpired?: boolean;
}

export default Vue.extend({
  components: {
    ButtonDefault,
    LoadingSpinner,
    CupertinoAlertDialog,
    PremiumFeatureIntroduce,
    FileRestoreOutlineIcon,
  },
  props: {
    title: {
      type: String,
      default: "",
    },
    isOpen: Boolean,
    dataType: String,
    dataId: String,
    novelId: String,
  },
  watch: {
    isOpen: {
      immediate: true,
      handler(open) {
        if (open) {
          this.trackHistoryEvent("history_dialog_open");
          this.fetchHistoryList();
        } else {
          this.isHistoryExpanded = true;
        }
      },
    },
    dataId: {
      immediate: true,
      handler(dataId) {
        if (dataId && this.isOpen) this.fetchHistoryList();
      },
    },
  },
  data() {
    return {
      history: null as HistoryItem[] | null,
      historyDetail: null as any | null,
      historyDetailLoading: false,
      historyItemId: null as string | null,
      requireUpgrade: false,
      isOpenRestoreConfirmDialog: false,
      fetchError: false,
      plan: null as Plan | null,
      isHistoryExpanded: true,
      nolaAnalytics: new NolaAnalytics(this.$gtm, this.$store),
    };
  },
  mounted() {
    API.get("NolaRestAPI", "/user/plan", {})
      .then((response) => {
        this.plan = response.plan;
      })
      .catch((e) => {
        console.error("プラン情報の取得に失敗しました", e);
        this.plan = Plan.free;
      });
  },
  methods: {
    trackHistoryEvent(eventName: string, extraParams?: Record<string, unknown>) {
      this.nolaAnalytics.logCustomEvent(eventName, {
        history_type: this.dataType,
        ...extraParams,
      });
    },
    onClickOutSide() {
      this.$emit("close");
    },
    toggleHistoryPanel() {
      this.isHistoryExpanded = !this.isHistoryExpanded;
    },
    closeRestoreConfirmDialog() {
      this.isOpenRestoreConfirmDialog = false;
    },
    async onConfirmRestore() {
      this.historyDetailLoading = true;
      try {
        await this.restoreHistory();
      } finally {
        this.historyDetailLoading = false;
        this.closeRestoreConfirmDialog();
      }
    },
    openRestoreConfirmDialog() {
      this.isOpenRestoreConfirmDialog = true;
    },
    async restoreHistory() {
      const { historyItemId } = this;
      if (!historyItemId) return;

      try {
        await API.post("NolaRestAPI", `/history/${historyItemId}/restore`, {
          headers: {
            "Content-Type": "application/json",
          },
          queryStringParameters: {
            dataType: this.dataType,
            novelId: this.novelId,
            dataId: this.dataId,
          },
        });
        this.trackHistoryEvent("history_restore");
        this.$emit("restore");
      } catch (e) {
        const error = e as any;
        if (error.response?.status === 403) {
          await showNotifyDialog({
            title: this.$t("historyManageDialog.restoreError.title"),
            content: this.$t("historyManageDialog.restoreError.premiumRequired"),
          });
        } else {
          await showNotifyDialog({
            title: this.$t("historyManageDialog.restoreError.title"),
            content: this.$t("historyManageDialog.restoreError.generic"),
          });
        }
      }
    },
    showRequireUpgradeDialog() {
      this.requireUpgrade = true;
    },
    async fetchHistoryDetail(historyId: string) {
      if (this.historyDetailLoading) return;

      const { dataType, novelId, dataId } = this;
      if (!dataType || !novelId || !dataId) return;

      this.historyDetailLoading = true;
      this.historyDetail = null;
      this.requireUpgrade = false;
      try {
        const response = await API.get("NolaRestAPI", `/history/${historyId}`, {
          queryStringParameters: {
            dataType,
            novelId,
            dataId,
            limit: 10,
          },
        });

        if (!response.url) {
          throw new Error("履歴詳細の取得に失敗しました。");
        }
        const detailResponse = await fetch(response.url);
        const detailData = await detailResponse.json();
        this.historyDetail = detailData;
        this.historyItemId = historyId;
        this.trackHistoryEvent("history_detail_view");
      } catch (e) {
        const error = e as any;

        if (error.response?.status === 403) {
          // プレミアム機能の導線を表示
          this.requireUpgrade = true;
        }

        this.historyDetail = null;
        this.historyItemId = null;
      } finally {
        this.historyDetailLoading = false;
      }
    },
    async fetchHistoryList() {
      const { dataType, novelId, dataId } = this;
      if (!dataType || !novelId || !dataId) return;
      try {
        this.history = null;
        this.requireUpgrade = false;
        this.fetchError = false;
        this.historyDetail = null;
        this.historyItemId = null;

        const historyItems: HistoryItem[] = [];
        let nextToken: string | undefined;
        do {
          // eslint-disable-next-line no-await-in-loop
          const response = await API.get("NolaRestAPI", "/history", {
            queryStringParameters: {
              dataType,
              novelId,
              dataId,
              ...(nextToken ? { nextToken } : {}),
            },
          });

          const { items, lastKey: nextLastKey } = response as { items?: HistoryItem[]; lastKey?: string };
          nextToken = nextLastKey;
          historyItems.push(...(items ?? []));
        } while (nextToken);

        this.history = historyItems || [];

        if (historyItems.length > 0) {
          const lastItem = historyItems[historyItems.length - 1];
          if (lastItem.restoreExpired) {
            this.showRequireUpgradeDialog();
          } else {
            this.fetchHistoryDetail(lastItem.historyId);
          }
        }
      } catch (e) {
        this.fetchError = true;
      }
    },
  },
  computed: {
    displayingHistoryList() {
      const { history } = this;
      if (!history) return null;
      let lastHistory: { year: number; restoreExpired?: boolean } | null = null;

      const result: Array<{
        message: string;
        displayNumber: number;
        id: string;
        timestamp: number;
        restoreExpired?: boolean;
        yearMessage?: string | null;
        restoreExpiredMessage?: string | null;
      }> = [];

      for (let i = history.length - 1, n = 0; i >= 0; i -= 1, n += 1) {
        const h = history[i];
        const date = new Date(h.timestamp);
        const year = date.getFullYear();

        const yearMessage =
          lastHistory && lastHistory.year !== year
            ? (this.$t("historyManageDialog.dateFormat.year", { year }) as string)
            : null;
        let restoreExpiredMessage: string | null = null;

        if (lastHistory && !lastHistory.restoreExpired && h.restoreExpired) {
          restoreExpiredMessage = this.$t("historyManageDialog.premiumGuide.restoreExpiredMessage");
        } else if (h.restoreExpired && i === history.length - 1) {
          restoreExpiredMessage = this.$t("historyManageDialog.premiumGuide.restoreExpiredMessage");
        }

        result.push({
          message: format(date, this.$t("historyManageDialog.dateFormat.timestamp") as string),
          displayNumber: n + 1,
          id: h.historyId,
          timestamp: h.timestamp,
          restoreExpired: h.restoreExpired,
          yearMessage,
          restoreExpiredMessage,
        });
        lastHistory = {
          year,
          restoreExpired: h.restoreExpired,
        };
      }
      return result;
    },
  },
});
